export const BUILT_IN_EXPENSE_CATEGORIES = [
    'Shipping',
    'Repairs',
    'Parts',
    'Reimbursements',
    'RentalReimbursement',
    'StorageFees',
    'Other',
];
export const isBuiltInExpenseCategory = (category) => BUILT_IN_EXPENSE_CATEGORIES.includes(category);
export const BUILT_IN_REPAIR_SUBCATEGORIES = [
    'Engine',
    'Transmission',
    'Engine replacement',
    'Transmission replacement',
    'Brakes',
    'Tires',
    'Anti theft',
    'Backup sensors',
    'Electrical',
    'Body',
    'Cameras',
    'Other',
];
/**
 * Default top-level repair categories, used to seed the `repairCategories` Firestore
 * collection on first read. Once seeded, all CRUD happens against Firestore.
 */
export const DEFAULT_REPAIR_CATEGORIES = [
    { key: 'powertrain', label: 'Powertrain', sortOrder: 10, iconKey: 'settings' },
    { key: 'suspension_steering', label: 'Suspension & Steering', sortOrder: 15, iconKey: 'spring' },
    { key: 'heating_air', label: 'Heating & Air', sortOrder: 18, iconKey: 'fan' },
    { key: 'brakes', label: 'Brakes', sortOrder: 20, iconKey: 'brake_disc' },
    { key: 'tires_wheels', label: 'Tires & Wheels', sortOrder: 30, iconKey: 'tire' },
    { key: 'electrical', label: 'Electrical & Lights', sortOrder: 40, iconKey: 'bolt' },
    { key: 'body_interior', label: 'Body & Interior', sortOrder: 50, iconKey: 'car' },
    { key: 'other', label: 'Other', sortOrder: 100, iconKey: 'other' },
];
export const OTHER_REPAIR_CATEGORY_KEY = 'other';
/**
 * Stable SKU prefixes for parts catalog items, keyed by repair category key.
 * Keep prefixes stable once parts exist in a category.
 */
export const REPAIR_CATEGORY_SKU_PREFIX = {
    powertrain: 'PWT',
    suspension_steering: 'SUS',
    heating_air: 'HVA',
    brakes: 'BRK',
    tires_wheels: 'TIR',
    electrical: 'ELC',
    body_interior: 'BDY',
    other: 'OTH',
};
export const getRepairCategorySkuPrefix = (categoryKey) => REPAIR_CATEGORY_SKU_PREFIX[categoryKey] ?? REPAIR_CATEGORY_SKU_PREFIX[OTHER_REPAIR_CATEGORY_KEY];
/** Built-in labels keyed by repair category key (fallback when Firestore is unavailable). */
export const DEFAULT_REPAIR_CATEGORY_LABELS = Object.fromEntries(DEFAULT_REPAIR_CATEGORIES.map(({ key, label }) => [key, label]));
export const getRepairCategoryLabel = (categoryKey, labelsByKey) => {
    if (labelsByKey instanceof Map) {
        const fromMap = labelsByKey.get(categoryKey);
        if (fromMap)
            return fromMap;
    }
    else if (labelsByKey) {
        const record = labelsByKey;
        if (record[categoryKey])
            return record[categoryKey];
    }
    return DEFAULT_REPAIR_CATEGORY_LABELS[categoryKey] ?? categoryKey;
};
/**
 * Curated icon keys available for categories. The actual React icon is resolved
 * client-side from this key. Keep strings stable — they are persisted on Firestore docs.
 */
export const REPAIR_CATEGORY_ICON_KEYS = [
    'settings',
    'brake_disc',
    'tire',
    'bolt',
    'car',
    'other',
    'build',
    'engine',
    'oil',
    'battery',
    'lightbulb',
    'fan',
    'thermostat',
    'spring',
    'shield',
    'camera',
    'lock',
    'gas',
    'water',
    'truck',
];
/** Default category for each built-in repair subcategory. */
export const DEFAULT_SUBCATEGORY_TO_CATEGORY_KEY = {
    Engine: 'powertrain',
    'Engine replacement': 'powertrain',
    Transmission: 'powertrain',
    'Transmission replacement': 'powertrain',
    Brakes: 'brakes',
    Tires: 'tires_wheels',
    Electrical: 'electrical',
    'Anti theft': 'electrical',
    'Backup sensors': 'electrical',
    Cameras: 'electrical',
    Body: 'body_interior',
    Other: 'other',
};
/**
 * Resolve a subcategory label (built-in or custom) to its top-level repair category key.
 * Falls back to `'other'` if the label is unknown or unmapped.
 */
export const getRepairCategoryKey = (subcategoryLabel, customSubcategories) => {
    const builtIn = BUILT_IN_REPAIR_SUBCATEGORIES.find(s => s === subcategoryLabel);
    if (builtIn)
        return DEFAULT_SUBCATEGORY_TO_CATEGORY_KEY[builtIn];
    const custom = customSubcategories.find(s => s.label === subcategoryLabel);
    if (custom?.categoryKey)
        return custom.categoryKey;
    return OTHER_REPAIR_CATEGORY_KEY;
};
